import type { ReactNode } from "react";

/** A session is document-local. Only the width preference is persisted. */
export interface SidebarEnvironment {
  ownerScope: string | null;
  path: string;
  capabilities: Readonly<Record<string, boolean>>;
}

export interface SidebarEntry<Payload = unknown> {
  key: string;
  payload: Payload;
  /** Write identity, including equal-value writes, for conditional rollback. */
  revision: number;
}

export interface SidebarDefinition<Input, Payload = Input> {
  id: string;
  label: string;
  order: number;
  group?: string;
  render?: (entry: SidebarEntry<Payload>) => ReactNode;
  isAvailable?: (environment: SidebarEnvironment) => boolean;
  strategy: {
    planOpen: (context: SidebarStrategyContext, input: Input) => SidebarChange;
    planClose?: (
      context: SidebarStrategyContext,
      entry: SidebarEntry<Payload>,
    ) => SidebarChange;
  };
}

/** Type erasure stays at the common registry boundary; features use typed tokens. */
export type RegisteredSidebarDefinition = SidebarDefinition<unknown, unknown>;

export interface SidebarRegistration {
  key: string;
  owner: symbol;
  scope: string | null;
  scopeToken: symbol;
}

export interface SidebarRegisteredPanel {
  definition: RegisteredSidebarDefinition;
  owners: ReadonlyMap<symbol, RegisteredSidebarDefinition>;
  implicit: boolean;
}

export interface SidebarSnapshot {
  entries: Readonly<Record<string, SidebarEntry>>;
  registrations: Readonly<Record<string, SidebarRegisteredPanel>>;
  activeKey: string | null;
  environment: SidebarEnvironment;
}

export interface SidebarWrite<Payload = unknown> {
  definition: RegisteredSidebarDefinition;
  entry: SidebarEntry<Payload>;
}

export interface SidebarChange {
  upsert?: readonly SidebarWrite[];
  remove?: readonly string[];
  /** Omit to preserve selection; null requests a surviving fallback. */
  select?: string | null;
  effects?: readonly (() => void)[];
}

export interface SidebarStrategyContext {
  snapshot: SidebarSnapshot;
  read<I, P>(definition: SidebarDefinition<I, P>): SidebarEntry<P> | undefined;
  write<I, P>(definition: SidebarDefinition<I, P>, payload: P): SidebarWrite<P>;
}

export interface SidebarOperation {
  signal: AbortSignal;
  isCurrent: () => boolean;
  dispose: () => void;
}

export function defineSidebar<I, P = I>(
  definition: SidebarDefinition<I, P>,
): SidebarDefinition<I, P> {
  return definition;
}

export function readSidebar<I, P>(
  snapshot: Pick<SidebarSnapshot, "entries">,
  definition: SidebarDefinition<I, P>,
): SidebarEntry<P> | undefined {
  return snapshot.entries[definition.id] as SidebarEntry<P> | undefined;
}

export function eraseSidebar<I, P>(
  definition: SidebarDefinition<I, P>,
): RegisteredSidebarDefinition {
  return definition as unknown as RegisteredSidebarDefinition;
}
